import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogContent, Typography,
  DialogContentText, Card, CardContent,
  DialogTitle,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Tooltip,
  Box,
  Alert, // Import Alert for Snackbar
  Checkbox, 
  FormControlLabel,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import DeleteIcon from '@mui/icons-material/Delete';
import TagModal from './components/TagModal';
import { addStyles, EditableMathField } from 'react-mathquill';
import { StaticMathField } from 'react-mathquill';
import StyleIcon from '@mui/icons-material/Style';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import sanitizeHtml from 'sanitize-html';
import { FaChevronDown, FaChevronUp } from "react-icons/fa"; // For the arrow icons
import { useContext } from 'react';
import { ThemeContext } from './context/ThemeContext';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from '@mui/material/CircularProgress';


<style jsx global>{`
  .ql-editor sub,
  .ql-editor sup {
    display: inline !important;
    vertical-align: baseline !important;
    font-size: 0.8em !important;
    line-height: 1 !important;
  }
  .ql-editor sub {
    bottom: -0.25em;
  }
  .ql-editor sup {
    top: -0.4em;
  }
`}</style>;


// === 1. Enable KaTeX formula button ===
const Formula = Quill.import("formats/formula");
if (Formula) {
  window.katex = katex;
}

// === 2. Secure paste (prevent XSS) ===
const Clipboard = Quill.import("modules/clipboard");
class SafeClipboard extends Clipboard {
  onPaste(e) {
    e.preventDefault();
    const html = (e.clipboardData || window.clipboardData).getData("text/html") ||
      (e.clipboardData || window.clipboardData).getData("text/plain");
    const clean = sanitizeHtml(html || "", {
      allowedTags: [
        "b",
        "i",
        "em",
        "strong",
        "u",
        "sub",
        "sup",
        "p",
        "br",
        "span",
        "ol",
        "ul",
        "li",
        "font",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "blockquote",
        "code",
        "pre",
      ],
      allowedAttributes: {
        "*": ["style", "class", "id"],
        span: ["style", "class"],
        font: ["color", "size", "face"],
      },
    });

    const range = this.quill.getSelection();
    if (range) {
      this.quill.clipboard.dangerouslyPasteHTML(range.index, clean || "<p><br></p>");
    }
  }
}
Quill.register("modules/clipboard", SafeClipboard);

// === 3. Rich editor for options (with math) ===
const RichInlineEditor = ({ value, onChange, placeholder = "" }) => {
  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ script: "sub" }, { script: "super" }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["formula"],
      ["clean"],
    ],
  };
  const formats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "script",
    "list",
    "bullet",
    "formula",
  ];




  return (
    <ReactQuill
      value={value || ""}
      onChange={onChange}
      modules={modules}
      formats={formats}
      placeholder={placeholder}
      theme="snow"
      style={{ marginBottom: "16px" }}
    />
  );
};



function App1() {
  const [cards, setCards] = useState([]);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['']);
  const [correctOption, setCorrectOption] = useState(null);
  const [currentExplanation, setCurrentExplanation] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false); //  state for duplicate confirmation
  const optionRefs = useRef([]);
  const { subfolder_id } = useParams();
  const [activeOptionIndex, setActiveOptionIndex] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false); //state for Snackbar
  const [snackbarMessage, setSnackbarMessage] = useState(''); //state for Snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState('error'); //state for Snackbar severity
  // Edit Modal States
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCardIndex, setEditCardIndex] = useState(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editOptions, setEditOptions] = useState(['']);
  const [editCorrectOption, setEditCorrectOption] = useState(null);
  const [editExplanation, setEditExplanation] = useState('');

  const [userGuideOpen, setUserGuideOpen] = useState(false);
const [viewModalOpen, setViewModalOpen] = useState(false);
const [viewCard, setViewCard] = useState(null);
const [showAnswer, setShowAnswer] = useState(false);

  const [collapsedQuestion, setCollapsedQuestion] = useState(false);
  const [collapsedOptions, setCollapsedOptions] = useState(false);
  const [collapsedExplanation, setCollapsedExplanation] = useState(false);
  const [editSelectedImage, setEditSelectedImage] = useState(null);

  // Delete Dialog States
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCardId, setDeleteCardId] = useState(null);

  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagCardIndex, setTagCardIndex] = useState(null);
  const [tagModalPosition, setTagModalPosition] = useState({ top: 0, left: 0 });
  const navigate = useNavigate();
  const { mode, customColors } = useContext(ThemeContext);
  // Popover Editor States
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [popoverContent, setPopoverContent] = useState('');
  const [editingField, setEditingField] = useState(null); // 'question' or 'explanation'
  const [selectionRange, setSelectionRange] = useState(null);

  const [optionExplanations, setOptionExplanations] = useState(['']);
  const [isCorrect, setIsCorrect] = useState([false]);
  const [editOptionExplanations, setEditOptionExplanations] = useState(['']);
  const [editIsCorrect, setEditIsCorrect] = useState([false]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editTags, setEditTags] = useState([]);
  const imageInputRef = useRef(null);

  // For new MCQ creation
  const [statement, setStatement] = useState("");
  const [answers, setAnswers] = useState([]);
  
const [tagsInput, setTagsInput] = useState("");
const [tags, setTags] = useState([]);
const [isLoading, setIsLoading] = useState(true);
const [isAssignedFolder, setIsAssignedFolder] = useState(false);
const [folderType, setFolderType] = useState(null);

  // Add to state declarations
  const [permissions, setPermissions] = useState({
    can_create_mcq: true,
    can_read_mcq: true,
    can_update_mcq: true,
    can_delete_mcq: true,
    can_start_flashcard: true,
    can_start_quiz: true,
    is_admin: false,
  });
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);

  const toggleQuestion = () => setCollapsedQuestion(!collapsedQuestion);
  const toggleOptions = () => setCollapsedOptions(!collapsedOptions);
  const toggleExplanation = () =>
    setCollapsedExplanation(!collapsedExplanation);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Refs to ReactQuill editors
  const questionQuillRef = useRef(null);
  const explanationQuillRef = useRef(null);

  const [dropdownVisible, setDropdownVisible] = useState(false); // Controls dropdown visibility
  const [selectedOption, setSelectedOption] = useState(''); // State to track selected option
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Update useEffect to fetch permissions
useEffect(() => {
   const initializeComponent = async () => {
     setIsLoading(true);
     try {
       const token = sessionStorage.getItem("authToken");
       if (!token) {
         showSnackbar("Access token is missing. Please log in again.", "error");
         setIsLoading(false);
         return;
       }

       // Step 1: Determine folder type (own vs assigned free/paid)
       const folderResponse = await axios.get(`${apiUrl}/folders/`, {
         headers: { Authorization: `Bearer ${token}` },
       });

       const currentSubfolderId = parseInt(subfolder_id);
       let isOwn = false;

       if (folderResponse.data.own_folders) {
         isOwn = folderResponse.data.own_folders.some(
           (folder) =>
             folder.id === currentSubfolderId ||
             (folder.subfolders &&
               folder.subfolders.some((sf) => sf.id === currentSubfolderId))
         );
       }

       if (isOwn) {
         setFolderType("own");
         setIsAssignedFolder(false);
       } else {
         let isFree = false;
         if (
           folderResponse.data.assigned_folders &&
           folderResponse.data.assigned_folders.free
         ) {
           isFree = folderResponse.data.assigned_folders.free.some(
             (folder) =>
               folder.id === currentSubfolderId ||
               (folder.subfolders &&
                 folder.subfolders.some((sf) => sf.id === currentSubfolderId))
           );
         }
         setFolderType(isFree ? "free" : "paid");
         setIsAssignedFolder(true);
       }

       // Step 2: Fetch permissions
       const userRole = sessionStorage.getItem("userRole");
       let permissionsData = {
         can_create_mcq: false,
         can_read_mcq: false,
         can_update_mcq: false,
         can_delete_mcq: false,
         can_start_flashcard: false,
         can_start_quiz: false,
         is_admin: false,
       };

       if (userRole === "admin") {
         permissionsData = {
           can_create_mcq: true,
           can_read_mcq: true,
           can_update_mcq: true,
           can_delete_mcq: true,
           can_start_flashcard: true,
           can_start_quiz: true,
           is_admin: true,
         };
       } else {
         const permResponse = await axios.get(`${apiUrl}/user/permissions/`, {
           headers: { Authorization: `Bearer ${token}` },
         });
         const {
           can_create_mcq,
           can_read_mcq,
           can_update_mcq,
           can_delete_mcq,
           can_start_flashcard,
           can_start_quiz,
         } = permResponse.data.permissions || {};

         permissionsData = {
           can_create_mcq: can_create_mcq || false,
           can_read_mcq: can_read_mcq || false,
           can_update_mcq: can_update_mcq || false,
           can_delete_mcq: can_delete_mcq || false,
           can_start_flashcard: can_start_flashcard || false,
           can_start_quiz: can_start_quiz || false,
           is_admin: false,
         };
       }
       setPermissions(permissionsData);

       // Step 3: Fetch flashcards if allowed (includes assigned folders)
       if (permissionsData.can_read_mcq || permissionsData.is_admin) {
         await fetchFlashcardsFromServer();
       } else if (isAssignedFolder) {
         // Assigned folders (free/paid) are readable even if permission flag is false
         await fetchFlashcardsFromServer();
       } else {
         showSnackbar("You don't have permission to view MCQs.", "warning");
       }
     } catch (error) {
       console.error("Failed to initialize component:", error);
       showSnackbar("Failed to load data. Please try again.", "error");
     } finally {
       setIsLoading(false);
     }
   };

   if (subfolder_id) {
     initializeComponent();
   }
 }, [subfolder_id, apiUrl]);

  // Update fetchFlashcardsFromServer
  // Updated useEffect for fetchFlashcardsFromServer (replace the entire function)
const fetchFlashcardsFromServer = async () => {
   try {
     const token = sessionStorage.getItem("authToken");
     if (!token) {
       showSnackbar("Access token is missing. Please log in again.", "error");
       return;
     }
     // FIX: added console.log to debug apiUrl and subfolder_id
     console.log("apiUrl:", apiUrl);
     console.log("subfolder_id:", subfolder_id);
     const response = await axios.get(`${apiUrl}/subfolder/${subfolder_id}/mcq/`, {
       headers: { Authorization: `Bearer ${token}` },
     });
     const fetchedCards = response.data.map((item) => {
       const answers = item.answers;
       return {
         id: item.id,
         question: item.statement,
         question_type: item.question_type,
         options: answers.map((answer) => answer.answer_text),
         optionExplanations: answers.map((answer) => answer.explanation || ""),
         isCorrect: answers.map((answer) => answer.is_correct),
         expanded: false,
         tags: item.tags,
         image: item.image || null,
       };
     });
     setCards(fetchedCards);
   } catch (error) {
     console.error("Failed to fetch flashcards from server", error);
     showSnackbar("Failed to fetch flashcards. Please try again.", "error");
   }
 };

  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, options.length);
  }, [options]);

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value; // Update the specific option by index
    setOptions(newOptions);
  };

  // Updated handleRemoveOption (replace the function)
  const handleRemoveOption = (index) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);

    const newExplanations = [...optionExplanations];
    newExplanations.splice(index, 1);
    setOptionExplanations(newExplanations);

    const newIsCorrect = [...isCorrect];
    newIsCorrect.splice(index, 1);
    setIsCorrect(newIsCorrect);
  };

  // Updated handleAddOption (replace the function)
  const handleAddOption = () => {
    setOptions(prev => [...prev, '']);
    setOptionExplanations(prev => [...prev, '']);
    setIsCorrect(prev => [...prev, false]);
  };
const openViewModal = (card) => {
  setViewCard(card);
  setShowAnswer(false);
  setViewModalOpen(true);
};
  // Updated handleOptionKeyPress (replace the function)
  const handleOptionKeyPress = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission

      // Add a new empty option and related fields
      setOptions((prevOptions) => [...prevOptions, ""]);
      setOptionExplanations((prev) => [...prev, ""]);
      setIsCorrect((prev) => [...prev, false]);

      // Focus the newly added option after the state is updated
      setTimeout(() => {
        const nextIndex = index + 1;
        if (optionRefs.current[nextIndex]) {
          optionRefs.current[nextIndex].focus();
        }
      }, 0);
    }
  };

  // New handlers (add these functions)
  const handleExplanationChange = (index, value) => {
    const newExplanations = [...optionExplanations];
    newExplanations[index] = value;
    setOptionExplanations(newExplanations);
  };

  const handleCorrectChange = (index, checked) => {
    const newIsCorrect = [...isCorrect];
    newIsCorrect[index] = checked;
    setIsCorrect(newIsCorrect);
  };

  // For edit modal
  const handleEditExplanationChange = (index, value) => {
    const newExplanations = [...editOptionExplanations];
    newExplanations[index] = value;
    setEditOptionExplanations(newExplanations);
  };

  const handleEditCorrectChange = (index, checked) => {
    const newIsCorrect = [...editIsCorrect];
    newIsCorrect[index] = checked;
    setEditIsCorrect(newIsCorrect);
  };

  const addEditOption = () => {
    setEditOptions(prev => [...prev, '']);
    setEditOptionExplanations(prev => [...prev, '']);
    setEditIsCorrect(prev => [...prev, false]);
  };

  const handleEditRemoveOption = (index) => {
    const newOptions = [...editOptions];
    newOptions.splice(index, 1);
    setEditOptions(newOptions);

    const newExplanations = [...editOptionExplanations];
    newExplanations.splice(index, 1);
    setEditOptionExplanations(newExplanations);

    const newIsCorrect = [...editIsCorrect];
    newIsCorrect.splice(index, 1);
    setEditIsCorrect(newIsCorrect);
  };

  const handleOptionsSelect = (index) => {
    const textArea = document.getElementById(`optionTextField_${index}`);
    if (textArea) {
      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;
      const selected = textArea.value.substring(start, end);
      setSelectedText(selected);
    }
  };

  const handleTextSelect = () => {
    const textArea = document.getElementById('questionTextField');
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selected = textArea.value.substring(start, end);
    setSelectedText(selected);
  };

  const handleExplanationSelect = () => {
    const textArea = document.getElementById('explanationTextField');
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selected = textArea.value.substring(start, end);
    setSelectedText(selected);
  };

  const renderContent = (html) => {
    if (!html) return null;

    // Split content into LaTeX and non-LaTeX parts
    const parts = html.split(/(\$\$.+?\$\$|\$.+?\$)/g);

    return (
      <div>
        {parts.map((part, index) => {
          // Handle block-level LaTeX ($$...$$)
          if (part.startsWith("$$") && part.endsWith("$$")) {
            const latex = part.slice(2, -2); // Remove $$ delimiters
            try {
              const renderedLatex = katex.renderToString(latex, { displayMode: true });
              return (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content block-latex"
                />
              );
            } catch (error) {
              console.error("Block LaTeX rendering error:", error);
              return <div key={index} className="error-content">{part}</div>;
            }
          }

          // Handle inline LaTeX ($...$)
          if (part.startsWith("$") && part.endsWith("$")) {
            const latex = part.slice(1, -1); // Remove $ delimiters
            try {
              const renderedLatex = katex.renderToString(latex, { displayMode: false });
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content inline-latex"
                />
              );
            } catch (error) {
              console.error("Inline LaTeX rendering error:", error);
              return <span key={index} className="error-content">{part}</span>;
            }
          }

          // Handle non-LaTeX content
          try {
            const formattedContent = part.replace(/\n/g, "<br />"); // Replace newlines
            const allowedTags = ["b", "i", "em", "strong", "span", "br", "u", "sub", "sup", "a", "p", "img"];
            const allowedAttributes = {
              img: ["src", "alt"],
              a: ["href", "target"],
              span: ["style"],
              p: ["style"],
            };

            // Use sanitize-html or DOMPurify with selective sanitization
            const sanitizedContent = sanitizeHtml(formattedContent, {
              allowedTags,
              allowedAttributes,
              transformTags: {
                'p': 'span', // Convert <p> tags to <span>
                'div': 'span', // Convert <div> tags to <span>
              },
            });

            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                className="rendered-content inline-text"
              />
            );
          } catch (error) {
            console.error("HTML rendering error:", error);
            return <span key={index} className="error-content">{part}</span>;
          }
        })}
      </div>
    );
  };



  // Utility function to strip HTML tags, preserving LaTeX
  const stripHtmlTags = (html) => {
    // First, preserve LaTeX by temporarily replacing it
    const latexPlaceholder = '___LATEX___';
    const latexExpressions = [];
    let index = 0;

    // Replace LaTeX expressions with placeholders
    const latexPreserved = html.replace(/(\$\$.+?\$\$|\$.+?\$)/g, (match) => {
      latexExpressions[index] = match;
      return `${latexPlaceholder}${index++}${latexPlaceholder}`;
    });

    // Sanitize the HTML, removing all tags except allowed ones
    const cleaned = sanitizeHtml(latexPreserved, {
      allowedTags: [
        "b",
        "i",
        "em",
        "strong",
        "u",
        "sub",
        "sup",
        "span",
        "br",
        "ol",
        "ul",
        "li",
        // add more as needed
      ],
      allowedAttributes: {
        "*": ["style", "class"],
      },
    });


    // Restore LaTeX expressions
    let restored = cleaned;
    latexExpressions.forEach((latex, i) => {
      restored = restored.replace(`${latexPlaceholder}${i}${latexPlaceholder}`, latex);
    });

    // Remove any remaining HTML entities (e.g., &nbsp;) and trim
    return restored
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .trim();
  };

  // Updated handleSaveQuestion (replace the function body)
  const handleSaveQuestion = async () => {
    if (!permissions.can_create_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to create an MCQ.", "warning");
      return;
    }

    // Validate
   if (
  question &&
  tags.length > 0 &&
  options.every((opt) => opt.trim() !== "") &&
  optionExplanations.every((exp) => exp.trim() !== "") &&
  isCorrect.some((correct) => correct)
) {
      const isDuplicate = cards.some(card => card.question === question);
      if (isDuplicate) {
        setDuplicateDialogOpen(true);
        return;
      }

      await saveQuestionToServer();
    } else {
      
        showSnackbar(
  'Please complete the question, tags, options, explanations, and select at least one correct option.',
  "warning"
);
      
    }
  };

  // Updated saveQuestionToServer using FormData
  const saveQuestionToServer = async () => {
    if (!permissions.can_create_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to create an MCQ.", "warning");
      return;
    }

    // Prepare answers array — send full rich HTML
    const answersData = options.map((opt, index) => ({
      answer_text: opt, // ← Full HTML with math, bold, etc.
      is_correct: isCorrect[index],
      explanation: optionExplanations[index], // ← Full HTML
    }));

    const formData = new FormData();
    formData.append("statement", question); // ← Full rich HTML
    formData.append("question_type", "MCQ");
    formData.append("answers", JSON.stringify(answersData));
    formData.append("tags", JSON.stringify(tags));

    if (selectedImage) {
      formData.append("image", selectedImage);
    }

    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        showSnackbar("Access token is missing. Please log in again.", "error");
        return;
      }

      const response = await axios.post(
        `${apiUrl}/subfolder/${subfolder_id}/mcq/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        fetchFlashcardsFromServer();

        // PERFECT CLEAN RESET
        setQuestion("");
        setTagsInput("");
        setOptions([]);
        setOptionExplanations([]);
        setIsCorrect([false]);

        setTimeout(() => {
          setOptions([""]);
          setOptionExplanations([""]);
          setIsCorrect([false]);
        }, 0);

        setTags([]);
        setSelectedImage(null);
        if (imageInputRef.current) imageInputRef.current.value = null;

        showSnackbar("Flashcard saved successfully!", "success");
      }
    } catch (error) {
      console.error("Error saving question:", error);
      showSnackbar("Failed to save the question.", "error");
    }
  };


  const handleConfirmDuplicate = async () => {
    if (!permissions.can_create_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to create an MCQ.", "warning");
      setDuplicateDialogOpen(false);
      return;
    }
    await saveQuestionToServer();
    setDuplicateDialogOpen(false);
  };

  const handleCancelDuplicate = () => {
    setDuplicateDialogOpen(false);
  };


  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (index === -1) {
        optionRefs.current[0]?.focus();
      } else if (index === options.length - 1) {
        setOptions([...options, '']);
        setTimeout(() => {
          optionRefs.current[options.length]?.focus();
        }, 0);
      } else {
        optionRefs.current[index + 1]?.focus();
      }
    }
  };

  const toggleCardExpand = (index) => {
    const newCards = [...cards];
    newCards[index].expanded = !newCards[index].expanded;
    setCards(newCards);
  };

  // Edit Modal Functions
  // Updated openEditModal (replace the function)
  const openEditModal = (index) => {
    if (!permissions.can_update_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to update an MCQ.", "warning");
      return;
    }
    setEditCardIndex(index);
    setEditQuestion(cards[index].question);
    setEditOptions([...cards[index].options]);
    setEditOptionExplanations([...cards[index].optionExplanations]);
    setEditIsCorrect([...cards[index].isCorrect]);
    setEditTags([...cards[index].tags]);
    setEditSelectedImage(cards[index].image); // Set the initial image
    setEditModalOpen(true);
  };

  // const handleEditOptionChange = (index, value) => {
  //   const newOptions = [...editOptions];
  //   newOptions[index] = value;
  //   setEditOptions(newOptions);
  // };
  const handleEditOptionChange = (index, value) => {
    setEditOptions(prevOptions => {
      const newOptions = [...prevOptions];
      newOptions[index] = value;
      return newOptions;
    });
  };


  // Updated handleUpdateCard (replace the function body)
  const handleUpdateCard = async () => {
    if (!permissions.can_update_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to update an MCQ.", "warning");
      return;
    }

   if (
  editQuestion &&
  editTags.length > 0 &&
  editOptions.every((opt) => opt.trim() !== "") &&
  editOptionExplanations.every((exp) => exp.trim() !== "") &&
  editIsCorrect.some((correct) => correct)
) {
      await updateQuestionOnServer();
    } else {
      showSnackbar(
        'Please complete the question, options, explanations, and select at least one correct option.',
        "warning"
      );
    }
  };

  const updateQuestionOnServer = async () => {
    const formData = new FormData();
    formData.append("statement", editQuestion);
    formData.append("question_type", "MCQ");
    formData.append("answers", JSON.stringify(
      editOptions.map((opt, index) => ({
        answer_text: opt,
        is_correct: editIsCorrect[index],
        explanation: editOptionExplanations[index],
      }))
    ));
    formData.append("tags", JSON.stringify(editTags));

    // Append image only if a new one is selected or if the existing one is removed
    if (editSelectedImage) {
      formData.append("image", editSelectedImage);
    } else if (editSelectedImage === null && cards[editCardIndex]?.image) {
      formData.append("image", ""); // Send empty to remove
    }

    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        showSnackbar("Access token is missing. Please log in again.", "error");
        return;
      }
      const cardId = cards[editCardIndex]?.id;
      if (!cardId) {
        showSnackbar("Card ID is missing. Cannot update.", "error");
        return;
      }
      const response = await axios.put(
        `${apiUrl}/subfolder/${subfolder_id}/mcq/${cardId}/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 200) {
        fetchFlashcardsFromServer();
        showSnackbar("Flashcard updated successfully!", "success");
        setEditModalOpen(false);
      }
    } catch (error) {
      console.error("Error updating question:", error);
      showSnackbar("Failed to update the question. Please try again.", "error");
    }
  };




  const handleCloseModal = () => {
    setEditModalOpen(false);
    setEditSelectedImage(null); // Reset image state
  };


  // Delete Dialog Functions
  const openDeleteDialog = (id) => {
    if (!permissions.can_delete_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to delete an MCQ.", "warning");
      return;
    }
    setDeleteCardId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCard = async () => {
    if (!permissions.can_delete_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to delete an MCQ.", "warning");
      return;
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        console.error('Access token is missing. Please log in again.');
        showSnackbar('Access token is missing. Please log in again.', "error");
        return;
      }
      const response = await axios.delete(`${apiUrl}/subfolder/${subfolder_id}/mcq/${deleteCardId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 200) {
        fetchFlashcardsFromServer();
        setDeleteDialogOpen(false);
        showSnackbar('Flashcard successfully deleted.', 'success');
      } else {
        showSnackbar('Failed to delete the flashcard. Please try again.', "error");
      }
    } catch (error) {
      console.error('Failed to delete flashcard from server', error);
      showSnackbar('Failed to delete the flashcard. Please try again.', "error");
    }
  };


  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  // Tag Modal Functions
  const openTagModal = (index, event) => {
    const iconRect = event.target.getBoundingClientRect();
    setTagCardIndex(index);
    setTagModalPosition({
      top: iconRect.bottom + window.scrollY,
      left: iconRect.left + window.scrollX,
    });
    setTagModalOpen(true);
  };

  const handleTagAdded = (cardId, newTag) => {
    if (!cardId || !newTag || !newTag.name) {
      console.error('Invalid tag data:', { cardId, newTag });
      showSnackbar('Invalid tag data. Please try again.', 'error');
      return;
    }

    try {
      const updatedCards = cards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            tags: [...card.tags, { name: newTag.name, id: newTag.id || null }],
          };
        }
        return card;
      });
      setCards(updatedCards);
      setTagModalOpen(false); // Close modal
    } catch (error) {
      console.error('Error updating local state in handleTagAdded:', error);
      showSnackbar('Failed to update tag locally: ' + error.message, 'error');
    }
  };

  const handleTagUpdated = (cardId, updatedTag) => {
    if (!cardId || !updatedTag || !updatedTag.id || !updatedTag.name) {
      console.error('Invalid tag update data:', { cardId, updatedTag });
      showSnackbar('Invalid tag update data. Please try again.', 'error');
      return;
    }

    try {
      const updatedCards = cards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            tags: card.tags.map((tag) =>
              tag.id === updatedTag.id ? { ...tag, name: updatedTag.name } : tag
            ),
          };
        }
        return card;
      });
      setCards(updatedCards);
    } catch (error) {
      console.error('Error updating local state in handleTagUpdated:', error);
      showSnackbar('Failed to update tag locally: ' + error.message, 'error');
    }
  };

  const handleTagDeleted = (cardId, tagId) => {
    if (!cardId || !tagId) {
      console.error('Invalid tag deletion data:', { cardId, tagId });
      showSnackbar('Invalid tag deletion data. Please try again.', 'error');
      return;
    }

    try {
      const updatedCards = cards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            tags: card.tags.filter((tag) => tag.id !== tagId),
          };
        }
        return card;
      });
      setCards(updatedCards);
    } catch (error) {
      console.error('Error updating local state in handleTagDeleted:', error);
      showSnackbar('Failed to delete tag locally: ' + error.message, 'error');
    }
  };

  // const handleIconClick = () => {
  //   //const hardCodedText = "mcqs"; // replace with your hard-coded text
  //   navigate(`/practiceparticularcard/${subfolder_id}/mcqs`);
  // };

  const handleIconClick = () => {
    // Toggle dropdown visibility when icon is clicked
    setDropdownVisible(!dropdownVisible);
  };

  // Dropdown change handler
  const handleDropdownChange = (option) => {
    setSelectedOption(option);

    const hardCodedText = "mcqs";

    if (option === "practiceCard" && !permissions.can_start_flashcard && !permissions.is_admin) {
      showSnackbar("You don't have permission to start Practice Flashcard.", "warning");
      return;
    }
    if (option === "quizPractice" && !permissions.can_start_quiz && !permissions.is_admin) {
      showSnackbar("You don't have permission to start Quiz Practice.", "warning");
      return;
    }

    if (option === "practiceCard") {
      navigate(`/practiceparticularcard/${subfolder_id}/${encodeURIComponent(hardCodedText)}`);
    } else if (option === "spacedrep") {
      navigate(`/practicespacedrep/${subfolder_id}/${encodeURIComponent(hardCodedText)}`);
    } else if (option === "quizPractice") {
      navigate(`/mcqQUIZ/${subfolder_id}/${encodeURIComponent(hardCodedText)}`);
    }

    setDropdownVisible(false);
  };

  return (
  <div className="App">
    {isLoading ? (
  <Box sx={{ p: 2 }}>
    {[...Array(4)].map((_, i) => (
      <Box
        key={i}
        sx={{
          mb: 2,
          p: 2,
          borderRadius: "16px",
          border: mode === "dark" ? "1px solid #333" : "1px solid #e0e7ff",
          backgroundColor: mode === "dark" ? "#2A2A3E" : "#fff",
        }}
      >
        {/* Question row */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Skeleton variant="rectangular" width={45} height={40} sx={{ borderRadius: "10px", mr: 2 }} />
          <Skeleton variant="text" width="70%" height={28} />
        </Box>
        {/* Tag chips */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton variant="rounded" width={60} height={22} />
          <Skeleton variant="rounded" width={80} height={22} />
          <Skeleton variant="rounded" width={50} height={22} />
        </Box>
      </Box>
    ))}
  </Box>
) : (
      <>
        {/* Header - always visible */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 3,
              fontWeight: "800",
              background:
                mode === "dark"
                  ? "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)"
                  : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Multiple Choice Questions
          </Typography>

          <div style={{ position: "relative" }}>
            <Tooltip title="User Guide">
              <IconButton
                onClick={() => setUserGuideOpen(true)}
                sx={{
                  color: mode === "dark" ? "#90caf9" : "#1e8bafff",
                  backgroundColor: mode === "dark" ? "rgba(144, 202, 249, 0.1)" : "rgba(30, 139, 175, 0.1)",
                  "&:hover": {
                    backgroundColor: mode === "dark" ? "rgba(144, 202, 249, 0.2)" : "rgba(30, 139, 175, 0.2)",
                  },
                }}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>

            {/* Dropdown only in own folders (optional - currently commented items) */}
            {!isAssignedFolder && dropdownVisible && (
              <div
                style={{
                  position: "absolute",
                  top: "30px",
                  right: "-30px",
                  zIndex: 1,
                  backgroundColor:
                    mode === "dark"
                      ? "#1E1E2E"
                      : mode === "custom"
                        ? customColors.background
                        : "#e3f2fd",
                  color:
                    mode === "dark"
                      ? "#E0E0E6"
                      : mode === "custom"
                        ? customColors.text
                        : "#000000",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "0",
                }}
              >
                {/* Your dropdown items here (currently commented in original code) */}
              </div>
            )}
          </div>
        </div>

        {/* Creation UI - only for own folders */}
        {!isAssignedFolder && (
          <div>
            {/* Question Card */}
            <Card
              sx={{
                mb: 2,
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff",
                border: "1px solid",
                borderColor: mode === "dark" ? "#333" : "#e0e7ff",
              }}
            >
              <CardContent>
                <div style={{ marginBottom: "10px" }}>
                  <div
                    style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    onClick={toggleQuestion}
                  >
                    <div style={{ marginRight: "10px" }}>
                      {collapsedQuestion ? <FaChevronDown /> : <FaChevronUp />}
                    </div>
                    <div>
                      Question <span style={{ color: "red" }}>*</span>
                    </div>
                  </div>
                  <div style={{ display: collapsedQuestion ? "none" : "block" }}>
                    <ReactQuill
                      ref={questionQuillRef}
                      value={question}
                      onChange={setQuestion}
                      placeholder="Enter your question here"
                      modules={{
                        toolbar: [
                          ["bold", "italic", "underline", "strike"],
                          ["blockquote", "code-block"],
                          [{ header: 1 }, { header: 2 }],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [{ script: "sub" }, { script: "super" }],
                          [{ indent: "-1" }, { indent: "+1" }],
                          [{ direction: "rtl" }],
                          [{ size: ["small", false, "large", "huge"] }],
                          [{ header: [1, 2, 3, 4, 5, 6, false] }],
                          [{ color: [] }, { background: [] }],
                          [{ font: [] }],
                          [{ align: [] }],
                          ["clean"],
                        ],
                      }}
                      formats={[
                        "header",
                        "font",
                        "size",
                        "bold",
                        "italic",
                        "underline",
                        "strike",
                        "blockquote",
                        "code-block",
                        "list",
                        "bullet",
                        "indent",
                        "link",
                        "image",
                        "video",
                        "color",
                        "background",
                        "align",
                        "script",
                        { name: "preserve-spaces", type: "inline" },
                      ]}
                      theme="snow"
                      style={{
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "pre-wrap",
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Options Card */}
            <div style={{ marginBottom: "10px" }}>
              <Card
                sx={{
                  mb: 2,
                  borderRadius: "12px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff",
                  border: "1px solid",
                  borderColor: mode === "dark" ? "#333" : "#e0e7ff",
                }}
              >
                <CardContent>
                  <div
                    style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    onClick={toggleOptions}
                  >
                    <div style={{ marginRight: "10px" }}>
                      {collapsedOptions ? <FaChevronDown /> : <FaChevronUp />}
                    </div>
                    <div>
                      Options <span style={{ color: "red" }}>*</span>
                    </div>
                  </div>
                  <div style={{ display: collapsedOptions ? "none" : "block" }}>
                    {options.map((option, index) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: "14px",
                          padding: "14px 16px",
                          borderRadius: "10px",
                          border: "1px solid rgba(209,213,219,0.9)",
                          backgroundColor: mode === "dark" ? "#020617" : "#f9fafb",
                          boxShadow:
                            mode === "dark"
                              ? "0 1px 3px rgba(15,23,42,0.8)"
                              : "0 1px 3px rgba(15,23,42,0.08)",
                        }}
                      >
                        <strong style={{ display: "block", marginBottom: "8px" }}>
                          Option {index + 1}
                        </strong>
                        <RichInlineEditor
                          value={option}
                          onChange={(val) => handleOptionChange(index, val)}
                          placeholder="Enter option text (supports H₂O, x², √x, formulas...)"
                        />
                        <div style={{ margin: "12px 0 8px" }}>
                          <strong>Explanation for this option:</strong>
                        </div>
                        <RichInlineEditor
                          value={optionExplanations[index]}
                          onChange={(val) => handleExplanationChange(index, val)}
                          placeholder="Why is this correct/incorrect?"
                        />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "10px",
                            gap: "15px",
                          }}
                        >
                          <label style={{ display: "flex", alignItems: "center" }}>
                            <input
                              type="checkbox"
                              checked={isCorrect[index]}
                              onChange={(e) =>
                                handleCorrectChange(index, e.target.checked)
                              }
                              style={{ marginRight: "8px" }}
                            />
                            <span>Correct Answer</span>
                          </label>
                          <IconButton
                            aria-label="delete"
                            onClick={() => handleRemoveOption(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      </div>
                    ))}
                    <Button
                      onClick={handleAddOption}
                      variant="outlined"
                      size="small"
                      sx={{ mt: 2 }}
                    >
                      + Add Option
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Image Upload */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                Upload Image (optional)
              </label>
              <input
                type="file"
                accept="image/*"
                ref={imageInputRef}
                onChange={(e) => setSelectedImage(e.target.files[0])}
                style={{
                  display: "block",
                  border: "1px solid #ccc",
                  padding: "6px",
                  borderRadius: "6px",
                  width: "100%",
                }}
              />
              {selectedImage && (
                <p
                  style={{
                    marginTop: "5px",
                    fontSize: "14px",
                    color:
                      mode === "dark"
                        ? "white"
                        : mode === "custom"
                          ? customColors?.text
                          : "green",
                  }}
                >
                  Selected: {selectedImage.name}
                </p>
              )}
            </div>
{/* Step 3: Add Tags */}
<div style={{ marginBottom: "20px" }}>
  <label
    style={{
      fontWeight: "bold",
      display: "block",
      marginBottom: "8px",
    }}
  >
    Step 3: Add Tags (Mandatory)
  </label>
<TextField
  fullWidth
  size="small"
  placeholder="Tags (comma-separated, e.g., Programming, IMAGES)"
  value={tagsInput}
  onChange={(e) => {
    const input = e.target.value;
    setTagsInput(input);

    const formattedTags = input
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag !== "")
      .map((tag) => ({ name: tag }));

    setTags(formattedTags);
  }}
/>
  
</div>
            {/* Add Flashcard Button */}
            <div
              onClick={() => {
                if (!(permissions.can_create_mcq || permissions.is_admin)) {
                  showSnackbar("You don't have permission to create an MCQ.", "warning");
                  return;
                }
                handleSaveQuestion();
              }}
              style={{
                display: "inline-block",
                cursor: permissions.can_create_mcq || permissions.is_admin ? "pointer" : "not-allowed",
                marginBottom: "24px",
              }}
            >
              <Button
                variant="contained"
                disabled={!(permissions.can_create_mcq || permissions.is_admin) || isLoadingPermissions}
                size="small"
                sx={{
                  background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                  color: "white",
                  padding: "6px 16px",
                  fontSize: "0.85rem",
                  fontWeight: "700",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "0 8px 25px rgba(30, 58, 138, 0.4)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)",
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 35px rgba(30, 58, 138, 0.5)",
                  },
                  "&.Mui-disabled": {
                    background: "#d1d5db",
                    color: "white",
                    opacity: 0.6,
                  },
                }}
                startIcon={<AddIcon />}
              >
                Add Flashcard
              </Button>
            </div>
          </div>
        )}

        {/* Card List - always shown if readable or assigned folder */}
        {(permissions.can_read_mcq || permissions.is_admin || isAssignedFolder) ? (
          <div>
            <ul className="card-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {cards.length > 0 ? (
                cards.map((card, index) => (
                  <li
                    key={index}
                    style={{
                      position: "relative",
                      padding: "16px",
                      borderRadius: "16px",
                      marginBottom: "16px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      maxWidth: "100%",
                      whiteSpace: "pre-wrap",
                      border: mode === "dark" ? "1px solid #333" : "1px solid #e0e7ff",
                      backgroundColor: mode === "dark" ? "#2A2A3E" : "#fff",
                      boxShadow: mode === "dark" ? "0 4px 14px rgba(0,0,0,0.6)" : "0 4px 12px rgba(15,23,42,0.08)",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow = mode === "dark"
                        ? "0 8px 30px rgba(0,0,0,0.75)"
                        : "0 8px 30px rgba(102, 126, 234, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = mode === "dark"
                        ? "0 4px 14px rgba(0,0,0,0.6)"
                        : "0 4px 12px rgba(15,23,42,0.08)";
                    }}
                  >
                    {/* Question Section */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        paddingRight: "40px",
                        maxWidth: "calc(100% - 70px)",
                        whiteSpace: "pre-wrap",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                          color: "white",
                          padding: "8px 14px",
                          borderRadius: "10px",
                          fontWeight: "800",
                          fontSize: "1.1rem",
                          marginRight: "16px",
                          minWidth: "45px",
                          textAlign: "center",
                          boxShadow: "0 4px 15px rgba(30, 58, 138, 0.3)",
                        }}
                      >
                        Q.{index + 1}
                      </div>
                      <span style={{ flex: 1, display: "flex", alignItems: "center" }}>
                        <strong>{renderContent(card.question)}</strong>
                      </span>
                    </div>

                    {/* Action Icons */}
                    <div
                      style={{
                        position: "absolute",
                        top: "20px",
                        right: "20px",
                        display: "flex",
                        gap: "14px",
                        zIndex: 10,
                        padding: "10px 14px",
                        borderRadius: "12px",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      {/* Expand icon - always */}
                      <i
                        className={`bi ${card.expanded ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"}`}
                        title={card.expanded ? "Contract" : "Expand"}
                        onClick={() => toggleCardExpand(index)}
                        style={{
                          cursor: "pointer",
                          fontSize: "1.2rem",
                          color: "#667eea",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#764ba2";
                          e.currentTarget.style.transform = "scale(1.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#667eea";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
<i
  className="bi bi-eye"
  title="View"
  onClick={() => openViewModal(card)}
  style={{
    cursor: "pointer",
    fontSize: "1.2rem",
    color: "#3b82f6",
    transition: "all 0.2s ease",
  }}
></i>
                      {/* Edit, Delete, Tag - only for own folders */}
                      {!isAssignedFolder && (
                        <>
                          <i
                            className="bi bi-pencil-square"
                            title="Edit"
                            onClick={() => openEditModal(index)}
                            style={{
                              cursor: permissions.can_update_mcq || permissions.is_admin ? "pointer" : "not-allowed",
                              opacity: permissions.can_update_mcq || permissions.is_admin ? 1 : 0.4,
                              fontSize: "1.2rem",
                              color: "#10b981",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (permissions.can_update_mcq || permissions.is_admin) {
                                e.currentTarget.style.color = "#059669";
                                e.currentTarget.style.transform = "scale(1.2)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = "#10b981";
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          ></i>
                          <i
                            className="bi bi-trash3"
                            title="Delete"
                            onClick={() => openDeleteDialog(card.id)}
                            style={{
                              cursor: permissions.can_delete_mcq || permissions.is_admin ? "pointer" : "not-allowed",
                              opacity: permissions.can_delete_mcq || permissions.is_admin ? 1 : 0.4,
                              fontSize: "1.2rem",
                              color: "#ef4444",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (permissions.can_delete_mcq || permissions.is_admin) {
                                e.currentTarget.style.color = "#dc2626";
                                e.currentTarget.style.transform = "scale(1.2)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = "#ef4444";
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          ></i>
                          <i
                            className="bi bi-tags"
                            title="Tag"
                            onClick={(e) => openTagModal(index, e)}
                            style={{
                              cursor: "pointer",
                              opacity: 1,
                              fontSize: "1.2rem",
                              color: "#8b5cf6",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = "#7c3aed";
                              e.currentTarget.style.transform = "scale(1.2)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = "#8b5cf6";
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          ></i>
                        </>
                      )}
                    </div>

                    {/* Expanded Content */}
                    {card.expanded && (
                      <>
                        {/* Image */}
                        {card.image && (
                          <div style={{ margin: "16px 0" }}>
                            <img
                              src={
                                typeof card.image === "string"
                                  // FIX: replaced hardcoded http://warals1.ddns.net:9017 with apiUrl
                                  ? `${apiUrl}${card.image}`
                                  : URL.createObjectURL(card.image)
                              }
                              alt="Flashcard Image"
                              style={{
                                maxWidth: "200px",
                                borderRadius: "8px",
                                cursor: "pointer",
                                border: mode === "dark" ? "1px solid #555" : "1px solid #ddd",
                              }}
                              onClick={() => {
                                // FIX: replaced hardcoded http://warals1.ddns.net:9017 with apiUrl
                                const imgUrl =
                                  typeof card.image === "string"
                                    ? `${apiUrl}${card.image}`
                                    : URL.createObjectURL(card.image);
                                window.open(imgUrl, "_blank");
                              }}
                            />
                            <button
                              style={{
                                marginTop: "8px",
                                padding: "6px 12px",
                                borderRadius: "6px",
                                border: mode === "dark" ? "1px solid #555" : "1px solid #ccc",
                                background: mode === "dark" ? "#333" : "#fff",
                                color: mode === "dark" ? "#fff" : "#333",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                // FIX: replaced hardcoded http://warals1.ddns.net:9017 with apiUrl
                                const imgUrl =
                                  typeof card.image === "string"
                                    ? `${apiUrl}${card.image}`
                                    : URL.createObjectURL(card.image);
                                window.open(imgUrl, "_blank");
                              }}
                            >
                              View Full Image
                            </button>
                          </div>
                        )}

                        {/* Options */}
                        <div style={{ margin: "8px 0 16px" }}>
                          <strong style={{ display: "block", marginBottom: "8px" }}>Options</strong>
                          <ul style={{ margin: 0, padding: 0, listStyleType: "none" }}>
                            {card.options.map((option, i) => {
                              const correct = card.isCorrect[i];
                              return (
                                <li
                                  key={i}
                                  style={{
                                    marginBottom: "12px",
                                    padding: "12px",
                                    borderRadius: "10px",
                                    border: correct
                                      ? "1px solid rgba(22, 163, 74, 0.3)"
                                      : "1px solid rgba(148, 163, 184, 0.45)",
                                    backgroundColor: correct
                                      ? mode === "dark"
                                        ? "rgba(22, 163, 74, 0.2)"
                                        : "rgba(220, 252, 231, 0.8)"
                                      : mode === "dark"
                                        ? "rgba(15, 23, 42, 0.7)"
                                        : "#f9fafb",
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "22px",
                                        height: "22px",
                                        borderRadius: "999px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "rgba(148, 163, 184, 0.25)",
                                        color: mode === "dark" ? "#e5e7eb" : "#111827",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {i + 1}
                                    </span>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                                        <span style={{ fontWeight: correct ? 600 : 400 }}>
                                          {renderContent(option)}
                                        </span>
                                        {correct && (
                                          <span
                                            style={{
                                              fontSize: "11px",
                                              textTransform: "uppercase",
                                              letterSpacing: "0.06em",
                                              color: "#16a34a",
                                              fontWeight: 600,
                                            }}
                                          >
                                            ✓ Correct
                                          </span>
                                        )}
                                      </div>
                                      {card.optionExplanations[i] && (
                                        <div
                                          style={{
                                            marginTop: "6px",
                                            paddingLeft: "4px",
                                            borderLeft: "2px solid rgba(148, 163, 184, 0.7)",
                                            fontSize: "13px",
                                            color: mode === "dark" ? "#e5e7eb" : "#4b5563",
                                          }}
                                        >
                                          {renderContent(card.optionExplanations[i])}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>

                        {/* Tags */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                          {card.tags?.map((tag) => (
                            <span
                              key={tag.id}
                              style={{
                                padding: "4px 8px",
                                borderRadius: "999px",
                                fontSize: "11px",
                                backgroundColor: mode === "dark" ? "#333" : "#f0f0f0",
                                color: mode === "dark" ? "#fff" : "#333",
                                border: mode === "dark" ? "1px solid #555" : "1px solid #ddd",
                              }}
                            >
                              #{tag.name}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </li>
                ))
              ) : (
                <li style={{ padding: "16px", textAlign: "center" }}>
                  <Typography>No MCQs available.</Typography>
                </li>
              )}
            </ul>
          </div>
        ) : (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            You don't have permission to view MCQs.
          </div>
        )}

      {/* Edit Modal */}
    <Dialog
  open={editModalOpen}
  onClose={handleCloseModal}
  fullWidth
  maxWidth="md"
  PaperProps={{
    sx: {
      borderRadius: '16px',
      background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.cardBg : '#ffffff',
      boxShadow: mode === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.4)'
        : '0 8px 32px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
    }
  }}
>
  {/* Gradient Header */}
  <Box sx={{
    background: mode === 'dark'
      ? 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)'
      : mode === 'custom'
        ? customColors.gradient
        : 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
    p: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: 'white',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{
        width: 48,
        height: 48,
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <i className="bi bi-pencil-square" style={{ fontSize: 28 }}></i>
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: '700', mb: 0.5 }}>
          Edit Flashcard
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Update your flashcard details below
        </Typography>
      </Box>
    </Box>
  </Box>

  {/* Content with improved styling */}
  <DialogContent sx={{
    p: 4,
    background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.background : '#ffffff',
   "&::-webkit-scrollbar": {
      width: "10px",
    },
    "&::-webkit-scrollbar-track": {
      background: mode === "dark" ? "#2d3748" : "#f1f5f9",
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: mode === "dark" ? "#475569" : "#94a3b8",
      borderRadius: "6px",
      border: `2px solid ${mode === "dark" ? "#2d3748" : "#f1f5f9"}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#64748b",
    },
    scrollbarWidth: "thin",
    scrollbarColor: mode === "dark"
      ? "#475569 #2d3748"
      : "#94a3b8 #f1f5f9",
  }}
>
  {/* Tags Section */}
<div style={{ marginBottom: "20px" }}>
  <label
    style={{
      fontWeight: "bold",
      display: "block",
      marginBottom: "8px",
    }}
  >
    Tags <span style={{ color: "red" }}>*</span>
  </label>

  <TextField
    fullWidth
    size="small"
    placeholder="Enter tags separated by comma (e.g. algebra, easy, class10)"
    onChange={(e) => {
      const value = e.target.value;

      const formattedTags = value
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag !== "")
        .map((tag) => ({ name: tag }));

      setTags(formattedTags);
    }}
  />
</div>
    {/* Image Upload Section */}
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle1"
        sx={{
          mb: 1.5,
          fontWeight: 600,
          color: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
        }} />
        Image (optional)
      </Typography>
      {/* Display the existing image if available */}
      {editSelectedImage && (
        <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
          <img
            src={
              typeof editSelectedImage === "string"
                // FIX: replaced hardcoded http://warals1.ddns.net:9017 with apiUrl
                ? `${apiUrl}${editSelectedImage}`
                : URL.createObjectURL(editSelectedImage)
            }
            alt="Flashcard"
            style={{
              maxWidth: "200px",
              maxHeight: "200px",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          />
          {/* Cross icon to remove the image */}
          <IconButton
            onClick={() => setEditSelectedImage(null)}
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              backgroundColor: 'white',
              borderRadius: '50%',
              padding: '4px',
            }}
          >
            <CancelIcon fontSize="small" color="error" />
          </IconButton>
        </Box>
      )}
      {/* Replace Image Button */}
      <Button
        variant="outlined"
        size="small"
        onClick={() => document.getElementById("edit-image-upload").click()}
        sx={{ mr: 2 }}
      >
        Add/Replace Image
      </Button>
      {/* Hidden file input for replacing the image */}
      <input
        id="edit-image-upload"
        type="file"
        accept="image/*"
        onChange={(e) => setEditSelectedImage(e.target.files[0])}
        style={{ display: 'none' }}
      />
    </Box>

    {/* Question Field */}
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle1"
        sx={{
          mb: 1.5,
          fontWeight: 600,
          color: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
        }} />
        Question <span style={{ color: '#ef4444' }}>*</span>
      </Typography>
      <Box className={`editor-wrapper ${mode}-mode`} sx={{ position: 'relative' }}>
        <ReactQuill
          value={editQuestion}
          onChange={setEditQuestion}
          placeholder="Enter your question here"
          theme="snow"
          modules={{
            toolbar: [
              ["bold", "italic", "underline", "strike"],
              ["blockquote", "code-block"],
              [{ header: 1 }, { header: 2 }],
              [{ list: "ordered" }, { list: "bullet" }],
              [{ script: "sub" }, { script: "super" }],
              [{ indent: "-1" }, { indent: "+1" }],
              [{ direction: "rtl" }],
              [{ size: ["small", false, "large", "huge"] }],
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              [{ color: [] }, { background: [] }],
              [{ font: [] }],
              [{ align: [] }],
              ["clean"],
            ],
          }}
          formats={[
            "header", "font", "size", "bold", "italic", "underline", "strike",
            "blockquote", "code-block", "list", "bullet", "indent", "link",
            "image", "video", "color", "background", "align", "script",
          ]}
        />
      </Box>
    </Box>
{/* Edit Tags */}
<Box sx={{ mb: 3 }}>
  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
    Tags <span style={{ color: "red" }}>*</span>
  </Typography>

  <TextField
    fullWidth
    size="small"
    value={editTags.map(tag => tag.name).join(", ")}
    onChange={(e) => {
      const value = e.target.value;

      const formattedTags = value
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag !== "")
        .map((tag) => ({ name: tag }));

      setEditTags(formattedTags);
    }}
  />
</Box>
    {/* Options with Explanations */}
    <Box>
      {editOptions.map((option, index) => (
        <Box
          key={index}
          sx={{
            mb: 2,
            p: 2,
            borderRadius: '10px',
            border: '1px solid rgba(209,213,219,0.9)',
            backgroundColor: mode === 'dark' ? '#020617' : '#f9fafb',
            boxShadow: mode === 'dark'
              ? '0 1px 3px rgba(15,23,42,0.8)'
              : '0 1px 3px rgba(15,23,42,0.08)',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Option {index + 1}
          </Typography>
          <RichInlineEditor
            value={option}
            onChange={(val) => handleEditOptionChange(index, val)}
            placeholder="Supports math, bold, H₂O, √x..."
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            Explanation:
          </Typography>
          <RichInlineEditor
            value={editOptionExplanations[index]}
            onChange={(val) => handleEditExplanationChange(index, val)}
            placeholder="Explain this option"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editIsCorrect[index]}
                  onChange={(e) => handleEditCorrectChange(index, e.target.checked)}
                />
              }
              label="Correct Answer"
            />
            <IconButton
              aria-label="delete"
              onClick={() => handleEditRemoveOption(index)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      ))}
      <Button
        onClick={addEditOption}
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
      >
        Add Option
      </Button>
    </Box>
  </DialogContent>

  {/* Enhanced Actions */}
  <DialogActions sx={{
    p: 3,
    gap: 2,
    background: mode === 'dark' ? '#2A2A3E' : mode === 'custom' ? customColors.background : '#f8f9fa',
  }}>
    <Button
      onClick={handleCloseModal}
      variant="outlined"
      sx={{
        borderColor: mode === 'dark' ? '#555' : mode === 'custom' ? customColors.border : '#d1d5db',
        color: mode === 'dark' ? '#A0A0A6' : mode === 'custom' ? customColors.textSecondary : '#6B7280',
        '&:hover': {
          borderColor: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
          background: 'transparent',
        }
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={handleUpdateCard}
      variant="contained"
      disabled={!permissions.can_update_mcq && !permissions.is_admin}
      sx={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
        color: 'white',
        fontWeight: 600,
        px: 4,
        '&:hover': {
          background: 'linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)',
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(30, 58, 138, 0.3)',
        },
        '&.Mui-disabled': {
          background: '#d1d5db',
          color: 'white',
          opacity: 0.6,
        },
        transition: 'all 0.3s ease',
      }}
    >
      Save Changes
    </Button>
  </DialogActions>
</Dialog>


      {/* Delete Dialog */}
    <Dialog
  open={deleteDialogOpen}
  onClose={handleCloseDeleteDialog}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: '20px',
      background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.cardBg : '#ffffff',
      boxShadow: mode === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.4)'
        : '0 8px 32px rgba(239, 68, 68, 0.2)',
      overflow: 'hidden',
    }
  }}
>
  {/* Warning Header */}
  <Box sx={{
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    p: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: 'white',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{
        width: 48,
        height: 48,
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* <i className="bi bi-exclamation-triangle" style={{ fontSize: 28 }}></i> */}
         <i className="bi bi-trash3" style={{ fontSize: 40 }}></i>

      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: '700', mb: 0.5 }}>
          Delete Flashcard
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          This action cannot be undone
        </Typography>
      </Box>
    </Box>
    {/* <IconButton
      onClick={handleCloseDeleteDialog}
      sx={{
        color: 'white',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
      }}
    >
      <CloseIcon />
    </IconButton> */}
  </Box>

  {/* Content */}
  <DialogContent sx={{
    p: 4,
    background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.background : '#ffffff',
  }}>
    <Box sx={{ textAlign: 'center', py: 2 }}>
      {/* <Box sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: mode === 'dark'
          ? 'rgba(239, 68, 68, 0.1)'
          : 'rgba(239, 68, 68, 0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
      }}>
       
      </Box> */}
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          color: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
          fontWeight: 600,
        }}
      >
        Are you sure you want to delete this flashcard?
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: mode === 'dark' ? '#A0A0A6' : mode === 'custom' ? customColors.textSecondary : '#6B7280',
          lineHeight: 1.6,
        }}
      >
        This will permanently remove the flashcard and all associated data. This action cannot be reversed.
      </Typography>
    </Box>
  </DialogContent>

  {/* Actions */}
  <DialogActions sx={{
    p: 3,
    gap: 2,
    background: mode === 'dark' ? '#2A2A3E' : mode === 'custom' ? customColors.background : '#f8f9fa',
  }}>
    <Button
      onClick={handleCloseDeleteDialog}
      variant="outlined"
      fullWidth
      sx={{
        borderColor: mode === 'dark' ? '#555' : mode === 'custom' ? customColors.border : '#d1d5db',
        color: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
        fontWeight: 600,
        '&:hover': {
          borderColor: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
          background: 'transparent',
        }
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={handleDeleteCard}
      variant="contained"
      fullWidth
      disabled={!permissions.can_delete_mcq && !permissions.is_admin}
      sx={{
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white',
        fontWeight: 600,
        '&:hover': {
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(239, 68, 68, 0.4)',
        },
        '&.Mui-disabled': {
          background: '#d1d5db',
          color: 'white',
          opacity: 0.6,
        },
        transition: 'all 0.3s ease',
      }}
    >
      Delete Flashcard
    </Button>
  </DialogActions>
</Dialog>


      {/* duplicate question modal*/}
      <Dialog open={duplicateDialogOpen} onClose={handleCancelDuplicate}>
        <DialogTitle>Duplicate Question</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A flashcard with this question already exists. Do you want to add it
            anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDuplicate}>Cancel</Button>
          <Button
            onClick={handleConfirmDuplicate}
            disabled={!permissions.can_create_mcq || permissions.is_admin}
          >
            Add Anyway
          </Button>
        </DialogActions>
      </Dialog>
      {/* snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {/* Tag Modal */}
      <TagModal
        open={tagModalOpen}
        position={tagModalPosition}
        onClose={() => setTagModalOpen(false)}
        subfolderId={subfolder_id}
        cardId={cards[tagCardIndex]?.id}
        cardQuestionType={cards[tagCardIndex]?.question_type}
        onTagAdded={handleTagAdded}
        onTagUpdated={handleTagUpdated}
        onTagDeleted={handleTagDeleted}
        onShowSnackbar={showSnackbar}
      />
   
{/* Dialog for viewing flashcard details */}
<Dialog
  open={viewModalOpen}
  onClose={() => {
    setViewModalOpen(false);
    setShowAnswer(false);
  }}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 18,
      overflow: "hidden",
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
    }
  }}
>
  <Box
    sx={{
      minHeight: 520,
      display: "flex",
      flexDirection: "column",
      p: { xs: 2, sm: 4 },
    }}
  >
    {/* Header */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 3,
        px: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: "rgba(255,255,255,0.2)",
          color: "#fff",
          padding: "8px 24px",
          borderRadius: 20,
          fontWeight: "bold",
          fontSize: "0.95rem",
        }}
      >
        Question 1 of 1
      </Box>
      <IconButton
        onClick={() => {
          setViewModalOpen(false);
          setShowAnswer(false);
        }}
        sx={{
          color: "#fff",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
        }}
      >
        <CloseIcon fontSize="large" />
      </IconButton>
    </Box>

    {/* White Content Area */}
    <Box
      sx={{
        backgroundColor: "#ffffff",
        borderRadius: 16,
        p: { xs: 4, sm: 5 },
        flex: 1,
        display: "flex",
        flexDirection: "column",
        boxShadow: "inset 0 2px 12px rgba(0,0,0,0.08)",
        color: "#000000",
        overflowY: "auto",
      }}
    >
      {/* Question - Minimal bottom margin */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          lineHeight: 1.5,
          textAlign: "center",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {viewCard && renderContent(viewCard.question)}
      </Typography>

      {/* Show Answer Button / Correct Answer + Explanation */}
      {!showAnswer ? (
        <Box sx={{ mt: "auto", display: "flex", justifyContent: "center"}}>
          <Button
            onClick={() => setShowAnswer(true)}
            sx={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
              color: "#fff",
              padding: "12px 36px",
              borderRadius: 30,
              fontWeight: "bold",
              textTransform: "none",
              boxShadow: "0 5px 14px rgba(102,126,234,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)",
              },
            }}
          >
            👁️ Show Answer
          </Button>
        </Box>
      ) : (
        <Box sx={{ mt: "auto", textAlign: "center" }}>
          {/* Correct Answer Section */}
          <Box
            sx={{
              background: "rgba(76,175,80,0.12)",
              border: "2px solid #4caf50",
              borderRadius: 12,
              p: 4,
              mb: 4,
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#2e7d32", fontWeight: 700, mb: 2 }}
            >
              ✓ Correct Answer
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                mb: 3,
                textAlign: "center" 
              }}
            >
              {viewCard?.options
                ?.filter((_, i) => viewCard.isCorrect[i])
                .map((opt, i, arr) => (
                  <span key={i}>
                    {renderContent(opt)}
                    {i !== arr.length - 1 ? " or " : ""}
                  </span>
                ))}
            </Typography>

            {/* Explanation */}
            <Typography
              variant="subtitle1"
              sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: "#1e8bafff",
                textAlign: "center"
              }}
            >
              Explanation
            </Typography>
            {viewCard?.optionExplanations
              ?.filter((_, i) => viewCard.isCorrect[i])
              .map((exp, i) => (
                <Box 
                  key={i} 
                  sx={{ 
                    fontSize: "1.05rem", 
                    lineHeight: 1.7,
                    textAlign: "center",
                    color: "#333"
                  }}
                >
                  {renderContent(exp)}
                </Box>
              ))}
          </Box>

          {/* Feedback Section */}
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, color: "#666", fontWeight: 600, mt: 2 }}
          >
            How well did you know this?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {["Skip", "Forgot", "Partial", "With Effort", "Easy"].map(
              (label, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: 20,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                  onClick={() => {}}
                >
                  {label}
                </Button>
              )
            )}
          </Box>
        </Box>
      )}
    </Box>
  </Box>
</Dialog>

   
   <Dialog
  open={userGuideOpen}
  onClose={() => setUserGuideOpen(false)}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: "16px",
      boxShadow: mode === "dark"
        ? "0 8px 32px rgba(0, 0, 0, 0.4)"
        : "0 8px 32px rgba(30, 139, 175, 0.15)",
      overflow: "hidden",
      background: mode === "dark"
        ? "#1E1B18"
        : mode === "custom"
          ? customColors.bg
          : "#ffffff",
    },
  }}
>
  {/* Header */}
  <Box
    sx={{
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
      p: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      color: "white",
    }}
  >
    <Box>
      <Typography variant="h5" sx={{ fontWeight: "700", mb: 0.5 }}>
        User Guide
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        Multiple Choice Questions
      </Typography>
    </Box>
    <IconButton
      onClick={() => setUserGuideOpen(false)}
      sx={{
        color: "white",
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
      }}
    >
      <CloseIcon />
    </IconButton>
  </Box>

  {/* Content */}
  <DialogContent
    sx={{
      p: 4,
      overflowY: "auto",
      maxHeight: "75vh",
      "&::-webkit-scrollbar": {
        width: "10px",
      },
      "&::-webkit-scrollbar-track": {
        background: mode === "dark"
          ? "#2d3748"
          : mode === "custom"
            ? customColors.scrollTrack
            : "#f1f5f9",
        borderRadius: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: mode === "dark"
          ? "#475569"
          : mode === "custom"
            ? customColors.scrollThumb
            : "#94a3b8",
        borderRadius: "6px",
        border: `2px solid ${
          mode === "dark"
            ? "#2d3748"
            : mode === "custom"
              ? customColors.border
              : "#f1f5f9"
        }`,
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: mode === "dark"
          ? "#64748b"
          : mode === "custom"
            ? customColors.scrollThumbHover
            : "#475569",
      },
      scrollbarWidth: "thin",
      scrollbarColor: mode === "dark"
        ? "#475569 #2d3748"
        : mode === "custom"
          ? `${customColors.scrollThumb} ${customColors.scrollTrack}`
          : "#94a3b8 #f1f5f9",
      background: mode === "dark"
        ? "#1E1B18"
        : mode === "custom"
          ? customColors.bg
          : "#ffffff",
      color: mode === "dark"
        ? "rgba(255, 255, 255, 0.87)"
        : mode === "custom"
          ? customColors.text
          : "rgba(30, 27, 24, 0.87)",
    }}
  >
    {/* Question Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-card-text" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Question
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Enter your question in the <strong>Question</strong> field. You can use rich text formatting, including bold, italics, and LaTeX for mathematical expressions.
      </Typography>
    </Box>

    {/* Options Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-list-ul" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Options
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Add multiple options for your question. You can mark one or more options as correct. Each option can have an explanation.
      </Typography>
    </Box>

    {/* Explanation Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-lightbulb" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Explanation
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Optionally, add an explanation for each option. This can provide additional context or details about the answer.
      </Typography>
    </Box>

    {/* Add Flashcard Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <AddIcon sx={{ color: "white", fontSize: 20 }} />
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Add Flashcard
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Click the <strong>Add Flashcard</strong> button to save your question, options, and explanations. If the question already exists, you'll be prompted to confirm.
      </Typography>
    </Box>

    {/* Flashcard List Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-card-list" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Flashcard List
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        View your list of flashcards below. You can <strong>expand</strong>, <strong>edit</strong>, <strong>delete</strong>, or <strong>tag</strong> each flashcard.
      </Typography>
    </Box>

    {/* Pro Tips */}
    <Box
      sx={{
        p: 3,
        borderRadius: "12px",
        bgcolor: mode === "dark"
          ? "rgba(144, 202, 249, 0.05)"
          : mode === "custom"
            ? customColors.bgSecondary
            : "rgba(30, 139, 175, 0.05)",
        border: mode === "dark"
          ? "1px solid rgba(144, 202, 249, 0.2)"
          : mode === "custom"
            ? `1px solid ${customColors.border}`
            : "1px solid rgba(30, 139, 175, 0.2)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "600",
          color: mode === "dark"
            ? "#90caf9"
            : mode === "custom"
              ? customColors.text
              : "#1e8bafff",
          mb: 2,
        }}
      >
        💡 Quick Tips
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Use the toolbar to format your question and options.
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Use LaTeX for mathematical expressions by enclosing them in `$...$` or `$$...$$`.
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Click the <strong>Expand/Collapse</strong> icon to show or hide flashcard details.
        </Typography>
      </Box>
    </Box>
  </DialogContent>
</Dialog>


    </>
    )}
  </div>
  );
}

export default App1;